import { DOCUMENT } from "@angular/common";
import {
  Component,
  effect,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { FuseNavigationItem } from "@fuse/components/navigation/navigation.types";
import { FuseConfig, FuseConfigService } from "@fuse/services/config";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { FusePlatformService } from "@fuse/services/platform";
import { FUSE_VERSION } from "@fuse/version";
import { FuseLayoutConfig } from "app/core/config/app.config";
import { NavigationService } from "app/core/navigation/navigation.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { FirebaseNotificationService } from "app/services/firebase-notification.service";
import { UserService } from "app/services/user.service";
import {
  isNotificationEnabled,
  isSsoEnabled,
} from "environments/environment.helper";
import { toast } from "ngx-sonner";
import { combineLatest, filter, firstValueFrom, map, Subject, takeUntil } from "rxjs";
import { EmptyLayoutComponent } from "./layouts/empty/empty.component";
import { MaterialLayoutComponent } from "./layouts/horizontal/material/material.component";
import { ClassicLayoutComponent } from "./layouts/vertical/classic/classic.component";
import { ClassyLayoutComponent } from "./layouts/vertical/classy/classy.component";
import { CompactLayoutComponent } from "./layouts/vertical/compact/compact.component";
import { DenseLayoutComponent } from "./layouts/vertical/dense/dense.component";
import { FuturisticLayoutComponent } from "./layouts/vertical/futuristic/futuristic.component";
import { ThinLayoutComponent } from "./layouts/vertical/thin/thin.component";
import { GeneralService } from "app/services/general.service";
import { OnboardingViewComponent } from "app/modules/onboarding-view/onboarding-view.component";

@Component({
  selector: "layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MaterialLayoutComponent,
    EmptyLayoutComponent,
    ClassicLayoutComponent,
    ClassyLayoutComponent,
    CompactLayoutComponent,
    DenseLayoutComponent,
    FuturisticLayoutComponent,
    ThinLayoutComponent,
    OnboardingViewComponent,
  ],
  standalone: true,
})
export class LayoutComponent implements OnInit, OnDestroy {
  config: FuseConfig;
  layout: string;
  scheme: "dark" | "light";
  theme: string;
  protected _unsubscribeAll: Subject<any> = new Subject<any>();

  @ViewChild("modalInstallIOS") viewModalInstallIOS: TemplateRef<any>;
  @ViewChild("modalAllowNotification")
  viewModalAllowNotification: TemplateRef<any>;

  protected toast = toast;

  navigation: FuseNavigationItem[];
  currentBrowserNotification: boolean;
  private _savingToken = false;
  isSsoEnabled = isSsoEnabled;
  isScreenSmall: boolean;
  drawerOpened: boolean = false;
  targetElement: Element;

  loginDisplay = this._userService.isLoginDisplay;

  /**
   * Constructor
   */
  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private readonly _document: any,
    private readonly _renderer2: Renderer2,
    private readonly _router: Router,
    private readonly _fuseConfigService: FuseConfigService,
    private readonly _fuseMediaWatcherService: FuseMediaWatcherService,
    private readonly _fusePlatformService: FusePlatformService,
    private readonly _navigationService: NavigationService,
    private readonly _dialog: MatDialog,
    private readonly _userService: UserService,
    private readonly generalService: GeneralService,
    private readonly _firebaseNotificationService: FirebaseNotificationService,
  ) {
    effect(() => {
      const user = this._userService.user();

      if (!user || user === undefined) {
        return;
      }

      const currentYear = new Date().getFullYear();

      // Only fetch if this year's holidays are not already cached —
      // prevents infinite re-fetching since getHolidayList updates
      // holidayDateList signal which can cause effect re-runs.
      const cached = this.generalService.holidayDateList();
      if (!cached?.[currentYear]) {
        this.generalService
          .getHolidayList(currentYear)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe();
      }

      if (user?.setting && isNotificationEnabled) {
        const isTokenAvailable = user?.setting.isTokenAvailable;
        const notificationState = user?.setting.notificationState
          ? user?.setting.notificationState
          : "default";

        this._firebaseNotificationService
          .checkSupport()
          .then((supported) => {
            if (supported) {
              this.checkNotificationPermission(isTokenAvailable, notificationState);
            } else {
              console.log("Push notifications are not supported in this environment.");
            }
          })
          .catch((err) => {
            console.error("Error checking notification support:", err);
          });
      }
    });
  }

  ngOnInit(): void {
    this.subscribeToNavigationData();
    this.showModalInstallPWAOnIOS();

    // Set the theme and scheme based on the configuration
    combineLatest([
      this._fuseConfigService.config$,
      this._fuseMediaWatcherService.onMediaQueryChange$([
        "(prefers-color-scheme: dark)",
        "(prefers-color-scheme: light)",
      ]),
    ])
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(([config, mql]) => {
          const options = {
            scheme: config.scheme,
            theme: config.theme,
          };

          // If the scheme is set to 'auto'...
          if (config.scheme === "auto") {
            // Decide the scheme using the media query
            options.scheme = mql.breakpoints["(prefers-color-scheme: dark)"]
              ? "dark"
              : "light";
          }

          return options;
        }),
      )
      .subscribe((options) => {
        // Store the options
        this.scheme = options.scheme;
        this.theme = options.theme;

        // Update the scheme and theme
        this._updateScheme();
        this._updateTheme();
      });

    // Subscribe to config changes
    this._fuseConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: FuseLayoutConfig) => {
        // Store the config
        this.config = config;

        // Update the layout
        this._updateLayout();
      });

    // Subscribe to NavigationEnd event
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => {
        // Update the layout
        this._updateLayout();
      });

    // Set the app version
    this._renderer2.setAttribute(
      this._document.querySelector("[ng-version]"),
      "fuse-version",
      FUSE_VERSION,
    );

    // Set the OS name
    this._renderer2.addClass(
      this._document.body,
      this._fusePlatformService.osName,
    );

    this.targetElement = document.querySelector("html");
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  private _updateLayout(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config.layout;

    // 2. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    const layoutFromQueryParam = route.snapshot.queryParamMap.get("layout");
    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }

    // 3. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      // Check if there is a 'layout' data
      if (
        path.routeConfig &&
        path.routeConfig.data &&
        path.routeConfig.data.layout
      ) {
        // Set the layout
        this.layout = path.routeConfig.data.layout;
      }
    });
  }

  /**
   * Update the selected scheme
   *
   * @private
   */
  private _updateScheme(): void {
    // Remove class names for all schemes
    this._document.body.classList.remove("light", "dark");

    // Add class name for the currently selected scheme
    this._document.body.classList.add(this.scheme);
  }

  /**
   * Update the selected theme
   *
   * @private
   */
  private _updateTheme(): void {
    // Find the class name for the previously selected theme and remove it
    this._document.body.classList.forEach((className: string) => {
      if (className.startsWith("theme-")) {
        this._document.body.classList.remove(
          className,
          className.split("-")[1],
        );
      }
    });

    // Add class name for the currently selected theme
    this._document.body.classList.add(this.theme);
  }

  /**
   * Subscribe to navigation data
   */

  private subscribeToNavigationData(): void {
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation.default;
      });
  }

  showIosInstallModal(localStorageKey: string): boolean {
    // detect if the device is on iOS
    const isIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone/.test(userAgent);
    };

    // check if the device is in standalone mode
    const isInStandaloneMode = () => {
      return (
        "standalone" in (window as any).navigator &&
        (window as any).navigator.standalone
      );
    };

    // show the modal only once
    const sessionStorageKeyValue = sessionStorage.getItem(localStorageKey);
    const iosInstallModalShown = sessionStorageKeyValue
      ? JSON.parse(sessionStorageKeyValue)
      : false;
    const shouldShowModalResponse =
      isIos() && !isInStandaloneMode() && !iosInstallModalShown;
    if (shouldShowModalResponse) {
      sessionStorage.setItem("ios", "true");
    }
    return shouldShowModalResponse;
  }

  /**
   * Method to show modal installation on ios
   */

  showModalInstallPWAOnIOS() {
    if (this.showIosInstallModal("ios")) {
      setTimeout(() => {
        this._dialog.open(this.viewModalInstallIOS, {
          panelClass: "my-css-class",
          width: "320px",
          height: "330px",
        });
      }, 1000);
    }
  }

  showModalAllowNotification() {
    setTimeout(() => {
      this._dialog.open(this.viewModalAllowNotification, {
        panelClass: "my-css-class",
        width: "320px",
        height: "330px",
      });
    }, 2000);
  }

  private _requestPermissionSafe(): Promise<NotificationPermission> {
    try {
      if (typeof window === "undefined" || !("Notification" in window)) {
        return Promise.reject("Notification API is not available.");
      }
      const result = Notification.requestPermission();
      if (result && typeof (result as any).then === "function") {
        return result;
      }
      return new Promise((resolve) => {
        Notification.requestPermission(resolve);
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  onAllowNotificationPermission() {
    this._requestPermissionSafe()
      .then((value) => {
        this._firebaseNotificationService.getToken();
        if (value === "granted") {
          this.onSaveToken("enabled");
        } else {
          console.error("Could not subscribe to notifications", value);
        }
        this._dialog.closeAll();
      })
      .catch((err) => {
        console.error("Failed to request notification permission:", err);
        this.toast.error("Could not request notification permission.");
        this._dialog.closeAll();
      });
  }

  closeAllowNotificationModal() {
    this._firebaseNotificationService
      .saveToken(null, "disabled")
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (result) => {
          if (result?.state !== 1) {
            this.toast.info("Failed to disable notification status");
          } else {
            this.updateStaffInfoSetting();
          }
          this._dialog.closeAll();
        },
        error: (err) => {
          console.error("Failed to save token on modal close:", err);
          this._dialog.closeAll();
        }
      });
  }

  checkNotificationPermission(isTokenAvailable, notificationState) {
    try {
      // Check if the browser supports notifications
      const permission = typeof Notification !== "undefined" ? Notification.permission : "default";
      if (permission !== "granted") {
        if (!isTokenAvailable && notificationState === "default") {
          this.showModalAllowNotification();
        }
      } else if (
        permission === "granted" &&
        !isTokenAvailable &&
        notificationState === "enabled"
      ) {
        this.onSaveToken(notificationState);
      }
    } catch (e) {
      console.error("Error checking notification permission:", e);
    }
  }

  async onSaveToken(notificationState: string) {
    // Guard against re-entrancy: the effect below re-runs whenever the user
    // signal changes (which updateStaffInfoSetting triggers), so without this a
    // single registration would fan out into a non-stop save loop.
    if (this._savingToken) {
      return;
    }
    this._savingToken = true;

    try {
      // Resolve a real FCM token before saving. Saving a null token would leave
      // IsTokenAvailable=false on the backend, so the effect would keep retrying.
      let token = this._firebaseNotificationService.notificationToken();
      if (!token) {
        token = await this._firebaseNotificationService.getToken();
      }

      if (!token) {
        // Couldn't obtain a token (permission denied / unsupported). Don't spam
        // the API with null saves — bail quietly.
        return;
      }

      const res: any = await firstValueFrom(
        this._firebaseNotificationService.saveToken(token, notificationState),
      );

      if (res?.state === 1) {
        this.updateStaffInfoSetting();
      } else {
        this.toast.info("Failed to enabled notification");
      }
    } catch (err) {
      console.error("Failed to save device token:", err);
    } finally {
      this._savingToken = false;
    }
  }

  updateStaffInfoSetting() {
    this._userService
      .getStaffInfo()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe();
  }
}
